import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "../../../intl";
import { formatStatusLabel } from "../../../utils/clientPortalFormat";
import {
  buildCandidateMapQuery,
  buildGoogleMapsEmbedUrl,
  buildGoogleMapsSearchUrl,
  buildGoogleSearchUrl,
  buildMailtoHref,
  buildTelHref,
  buildWebsiteHref,
  getCandidateDiscoveryMeta,
  getCandidateNormalizationInsights,
} from "../../../utils/vendorDirectory/candidatePublicProfile";
import {
  formatReputationRating,
  getCandidateReputationSignals,
} from "../../../utils/vendorDirectory/candidateReputation";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.candidates.detail";

function FieldRow({ label, children }) {
  if (!children) return null;
  return (
    <div className="cvd-public-profile__field">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function ExternalLink({ href, children }) {
  if (!href) return children || "—";
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function MatchStatusBadge({ status, t }) {
  if (!status) return null;
  const variant =
    status === "matched" ? "published" : status === "unmatched" ? "warning" : "draft";
  return (
    <span className={`cvd-badge cvd-badge--${variant}`}>
      {t(`${COPY_PREFIX}.matchStatus.${status}`, {}, formatStatusLabel(status))}
    </span>
  );
}

function ReputationStars({ rating, label }) {
  const value = Number(rating) || 0;
  const stars = Array.from({ length: 5 }, (_, index) => {
    const threshold = index + 1;
    if (value >= threshold) return "full";
    if (value >= threshold - 0.5) return "half";
    return "empty";
  });

  return (
    <span className="cvd-public-profile__stars" aria-label={label}>
      {stars.map((state, index) => (
        <span
          key={index}
          className={`cvd-public-profile__star cvd-public-profile__star--${state}`}
          aria-hidden="true"
        >
          ★
        </span>
      ))}
    </span>
  );
}

function ReputationMetric({ label, value }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div className="cvd-public-profile__metric">
      <span className="cvd-public-profile__metric-label">{label}</span>
      <span className="cvd-public-profile__metric-value">{value}</span>
    </div>
  );
}

function TrustBandBadge({ band, t }) {
  if (!band || band === "none") return null;
  const variant =
    band === "strong" ? "published" : band === "good" ? "published" : band === "moderate" ? "warning" : "draft";
  return (
    <span className={`cvd-badge cvd-badge--${variant}`}>
      {t(`${COPY_PREFIX}.reputationBand.${band}`, {}, formatStatusLabel(band))}
    </span>
  );
}

export default function VendorCandidatePublicProfilePanel({
  candidate,
  categoryLabel,
  municipalityLabel,
  serviciosBase,
}) {
  const { t, locale } = useLocale();
  const insights = useMemo(() => getCandidateNormalizationInsights(candidate), [candidate]);
  const discovery = useMemo(() => getCandidateDiscoveryMeta(candidate), [candidate]);
  const reputation = useMemo(() => getCandidateReputationSignals(candidate), [candidate]);
  const mapQuery = useMemo(() => buildCandidateMapQuery(candidate), [candidate]);
  const mapsSearchUrl = buildGoogleMapsSearchUrl(mapQuery);
  const mapsEmbedUrl = buildGoogleMapsEmbedUrl(mapQuery, locale);
  const googleSearchUrl = buildGoogleSearchUrl(
    [candidate?.rawName, municipalityLabel !== "—" ? municipalityLabel : candidate?.rawMunicipality]
      .filter(Boolean)
      .join(" ")
  );
  const phoneHref = buildTelHref(insights.phoneDisplay);
  const emailHref = buildMailtoHref(insights.emailDisplay);
  const websiteHref = buildWebsiteHref(insights.websiteDisplay);
  const ratingLabel =
    reputation.rating != null
      ? t(`${COPY_PREFIX}.ratingAria`, {
          rating: formatReputationRating(reputation.rating, locale),
          count: reputation.reviewCount ?? 0,
        })
      : null;
  const reviewCountLabel =
    reputation.reviewCount != null
      ? t(`${COPY_PREFIX}.reviewCountLabel`, { count: reputation.reviewCount })
      : null;
  const ratingsCountLabel =
    reputation.ratingsCount != null
      ? t(`${COPY_PREFIX}.ratingsCountLabel`, { count: reputation.ratingsCount })
      : null;
  const commentCountLabel =
    reputation.commentCount != null
      ? t(`${COPY_PREFIX}.commentCountLabel`, { count: reputation.commentCount })
      : null;
  const yearsLabel =
    reputation.yearsInBusiness != null
      ? t(`${COPY_PREFIX}.yearsInBusinessLabel`, { count: reputation.yearsInBusiness })
      : reputation.yearEstablished != null
        ? String(reputation.yearEstablished)
        : null;

  return (
    <section className="cvd-public-profile" aria-labelledby="candidate-public-profile-title">
      <div className="cvd-public-profile__header">
        <div>
          <h2 id="candidate-public-profile-title">{t(`${COPY_PREFIX}.publicProfileTitle`)}</h2>
          <p className="cvd-public-profile__hint">{t(`${COPY_PREFIX}.publicProfileHint`)}</p>
        </div>
        <div className="cvd-public-profile__lookup-links">
          {mapsSearchUrl && (
            <a className="cvd-btn cvd-btn--secondary" href={mapsSearchUrl} target="_blank" rel="noopener noreferrer">
              {t(`${COPY_PREFIX}.openGoogleMaps`)}
            </a>
          )}
          {googleSearchUrl && (
            <a className="cvd-btn cvd-btn--ghost" href={googleSearchUrl} target="_blank" rel="noopener noreferrer">
              {t(`${COPY_PREFIX}.searchGoogle`)}
            </a>
          )}
        </div>
      </div>

      <div className="cvd-public-profile__layout">
        <div className="cvd-public-profile__details">
          <article className="cvd-public-profile__card cvd-public-profile__card--reputation">
            <div className="cvd-public-profile__card-heading">
              <div>
                <h3>{t(`${COPY_PREFIX}.reputationTitle`)}</h3>
                <p className="cvd-public-profile__card-hint">{t(`${COPY_PREFIX}.reputationHint`)}</p>
              </div>
              <TrustBandBadge band={reputation.trustBand} t={t} />
            </div>

            {reputation.hasData ? (
              <>
                <div className="cvd-public-profile__reputation-hero">
                  {reputation.rating != null ? (
                    <div className="cvd-public-profile__rating-block">
                      <ReputationStars rating={reputation.rating} label={ratingLabel} />
                      <p className="cvd-public-profile__rating-value">
                        {formatReputationRating(reputation.rating, locale)}
                        <span className="muted"> / 5</span>
                      </p>
                    </div>
                  ) : (
                    <p className="cvd-public-profile__rating-missing">
                      {t(`${COPY_PREFIX}.reputationRatingMissing`)}
                    </p>
                  )}
                  {reputation.ratingSource ? (
                    <p className="cvd-public-profile__rating-source">
                      {t(`${COPY_PREFIX}.reputationSource`, { source: reputation.ratingSource })}
                    </p>
                  ) : null}
                </div>

                <div className="cvd-public-profile__metrics">
                  <ReputationMetric label={t(`${COPY_PREFIX}.reviewCount`)} value={reviewCountLabel} />
                  {reputation.ratingsCount != null &&
                  reputation.reviewCount != null &&
                  reputation.ratingsCount !== reputation.reviewCount ? (
                    <ReputationMetric label={t(`${COPY_PREFIX}.ratingsCount`)} value={ratingsCountLabel} />
                  ) : reputation.ratingsCount != null && reputation.reviewCount == null ? (
                    <ReputationMetric label={t(`${COPY_PREFIX}.ratingsCount`)} value={ratingsCountLabel} />
                  ) : null}
                  <ReputationMetric label={t(`${COPY_PREFIX}.commentCount`)} value={commentCountLabel} />
                  <ReputationMetric
                    label={t(`${COPY_PREFIX}.yearsInBusiness`)}
                    value={yearsLabel}
                  />
                  {reputation.priceLevel != null ? (
                    <ReputationMetric
                      label={t(`${COPY_PREFIX}.priceLevel`)}
                      value={"$".repeat(Math.min(5, Math.max(1, reputation.priceLevel)))}
                    />
                  ) : null}
                  {reputation.sourceVerified != null ? (
                    <ReputationMetric
                      label={t(`${COPY_PREFIX}.sourceVerified`)}
                      value={
                        reputation.sourceVerified
                          ? t(`${COPY_PREFIX}.sourceVerifiedYes`)
                          : t(`${COPY_PREFIX}.sourceVerifiedNo`)
                      }
                    />
                  ) : null}
                </div>

                <p className="cvd-public-profile__reputation-footnote">
                  {t(`${COPY_PREFIX}.reputationFootnote.${reputation.trustBand}`, {}, "")}
                </p>
              </>
            ) : (
              <div className="cvd-public-profile__reputation-empty">
                <p>{t(`${COPY_PREFIX}.reputationEmpty`)}</p>
                <p className="muted">{t(`${COPY_PREFIX}.reputationEmptyHint`)}</p>
                {googleSearchUrl ? (
                  <a className="cvd-btn cvd-btn--secondary" href={googleSearchUrl} target="_blank" rel="noopener noreferrer">
                    {t(`${COPY_PREFIX}.searchGoogle`)}
                  </a>
                ) : null}
              </div>
            )}
          </article>

          <article className="cvd-public-profile__card">
            <h3>{t(`${COPY_PREFIX}.identityTitle`)}</h3>
            <dl className="cvd-public-profile__grid">
              <FieldRow label={t(`${COPY_PREFIX}.businessName`)}>
                {candidate?.rawName || candidate?.normalizedName || "—"}
              </FieldRow>
              {candidate?.normalizedName && candidate.normalizedName !== candidate?.rawName && (
                <FieldRow label={t(`${COPY_PREFIX}.normalizedName`)}>
                  {candidate.normalizedName}
                </FieldRow>
              )}
              <FieldRow label={t(`${COPY_PREFIX}.category`)}>
                <span className="cvd-public-profile__inline">
                  {categoryLabel}
                  <MatchStatusBadge status={insights.categoryStatus} t={t} />
                </span>
              </FieldRow>
              {insights.categoryRaw && insights.categoryRaw !== categoryLabel && (
                <FieldRow label={t(`${COPY_PREFIX}.rawCategory`)}>{insights.categoryRaw}</FieldRow>
              )}
              <FieldRow label={t(`${COPY_PREFIX}.municipality`)}>
                <span className="cvd-public-profile__inline">
                  {municipalityLabel}
                  <MatchStatusBadge status={insights.municipalityStatus} t={t} />
                </span>
              </FieldRow>
              {insights.vendorKindHint && (
                <FieldRow label={t(`${COPY_PREFIX}.businessType`)}>
                  {t(`${COPY_PREFIX}.vendorKind.${insights.vendorKindHint}`, {}, formatStatusLabel(insights.vendorKindHint))}
                </FieldRow>
              )}
            </dl>
          </article>

          <article className="cvd-public-profile__card">
            <h3>{t(`${COPY_PREFIX}.contactTitle`)}</h3>
            <dl className="cvd-public-profile__grid">
              <FieldRow label={t(`${COPY_PREFIX}.phone`)}>
                {phoneHref ? <a href={phoneHref}>{insights.phoneDisplay}</a> : insights.phoneDisplay || "—"}
              </FieldRow>
              <FieldRow label={t(`${COPY_PREFIX}.email`)}>
                {emailHref ? <a href={emailHref}>{insights.emailDisplay}</a> : insights.emailDisplay || "—"}
              </FieldRow>
              <FieldRow label={t(`${COPY_PREFIX}.website`)}>
                {websiteHref ? (
                  <ExternalLink href={websiteHref}>{insights.websiteDisplay}</ExternalLink>
                ) : (
                  "—"
                )}
              </FieldRow>
              <FieldRow label={t(`${COPY_PREFIX}.address`)}>
                {insights.addressDisplay ? (
                  mapsSearchUrl ? (
                    <ExternalLink href={mapsSearchUrl}>{insights.addressDisplay}</ExternalLink>
                  ) : (
                    insights.addressDisplay
                  )
                ) : (
                  "—"
                )}
              </FieldRow>
              <FieldRow label={t(`${COPY_PREFIX}.neighborhood`)}>
                {insights.neighborhoodName || "—"}
              </FieldRow>
              <FieldRow label={t(`${COPY_PREFIX}.geocode`)}>
                {insights.geocodeDisplay || "—"}
              </FieldRow>
            </dl>
          </article>

          <article className="cvd-public-profile__card">
            <h3>{t(`${COPY_PREFIX}.discoveryTitle`)}</h3>
            <dl className="cvd-public-profile__grid">
              <FieldRow label={t(`${COPY_PREFIX}.source`)}>
                {candidate?.sourceName || candidate?.sourceId || "—"}
              </FieldRow>
              {discovery.sourceUrl && (
                <FieldRow label={t(`${COPY_PREFIX}.sourceUrl`)}>
                  <ExternalLink href={discovery.sourceUrl}>{discovery.sourceUrl}</ExternalLink>
                </FieldRow>
              )}
              {discovery.discoveryQuery && (
                <FieldRow label={t(`${COPY_PREFIX}.discoveryQuery`)}>{discovery.discoveryQuery}</FieldRow>
              )}
              {discovery.discoveryOrigin && (
                <FieldRow label={t(`${COPY_PREFIX}.discoveryOrigin`)}>
                  {t(`${COPY_PREFIX}.discoveryOriginValue.${discovery.discoveryOrigin}`, {}, discovery.discoveryOrigin)}
                </FieldRow>
              )}
              {discovery.discoveryCategoryKey && (
                <FieldRow label={t(`${COPY_PREFIX}.discoveryCategory`)}>
                  {discovery.discoveryCategoryKey}
                </FieldRow>
              )}
              {discovery.discoveryMunicipalityName && (
                <FieldRow label={t(`${COPY_PREFIX}.discoveryMunicipality`)}>
                  {discovery.discoveryMunicipalityName}
                </FieldRow>
              )}
              {candidate?.sourceRecordId && (
                <FieldRow label={t(`${COPY_PREFIX}.sourceRecordId`)}>{candidate.sourceRecordId}</FieldRow>
              )}
              {candidate?.importJobId && (
                <FieldRow label={t(`${COPY_PREFIX}.import`)}>
                  <Link to={`${serviciosBase}/importaciones/${candidate.importJobId}`}>
                    {candidate.importFilename || candidate.importJobId}
                  </Link>
                </FieldRow>
              )}
              {discovery.collectionRunId &&
                candidate?.collectionRunId === discovery.collectionRunId && (
                <FieldRow label={t(`${COPY_PREFIX}.collectionRun`)}>
                  <Link to={`${serviciosBase}/colecciones/ejecuciones/${discovery.collectionRunId}`}>
                    {discovery.collectionRunId}
                  </Link>
                </FieldRow>
              )}
            </dl>
          </article>
        </div>

        <aside className="cvd-public-profile__map-panel">
          <h3>{t(`${COPY_PREFIX}.mapTitle`)}</h3>
          <p className="cvd-public-profile__map-hint">{t(`${COPY_PREFIX}.mapHint`)}</p>
          {mapsEmbedUrl ? (
            <>
              <div className="cvd-public-profile__map-frame">
                <iframe
                  title={t(`${COPY_PREFIX}.mapTitle`)}
                  src={mapsEmbedUrl}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <p className="cvd-public-profile__map-query muted">{mapQuery}</p>
              <a className="cvd-btn cvd-btn--secondary" href={mapsSearchUrl} target="_blank" rel="noopener noreferrer">
                {t(`${COPY_PREFIX}.openGoogleMaps`)}
              </a>
            </>
          ) : (
            <p className="empty-state">{t(`${COPY_PREFIX}.mapUnavailable`)}</p>
          )}
        </aside>
      </div>
    </section>
  );
}
